import React from "react";
import {observer} from "mobx-react";
import {StandardDialogPageComponent} from "../../../../../components/modal/page/standard-dialog-page.component";
import {
    StandardDialogContentComponent
} from "../../../../../components/modal/content/standard-dialog-content.component";
import {StandardDialogFooterComponent} from "../../../../../components/modal/footer/standard-dialog-footer.component";
import {IDialogConfirmationHandler} from "../../../../../services/dialog/dialog.service.interface";
import {IBookingViewModel} from "../../../../../services/booking/models/booking-view-model.interface";
import {
    IInsuranceRefundDetailsResponse
} from "../../../../../services/airline-webapi/responses/insurance-refund-details.response";
import styled from "styled-components";
import {PriceComponent} from "../../../../../components/price/price.component";
import {NeutralButton} from "../../../../../basic-components/buttons/neutral-button";
import {useServices} from "../../../../../hooks/use-services.hook";
import {PrimaryButton} from "../../../../../basic-components/buttons/primary-button";
import {CardBox} from "../../../../../basic-components/card/card.box";
import {IonIcon} from "@ionic/react";
import {
    arrowForwardOutline,
    calendarOutline,
    informationCircleOutline,
    repeatOutline
} from "ionicons/icons";
import {StandardDialogHeaderComponent} from "../../../../../components/modal/header/standard-dialog-header.component";
import {InsuranceClaimConfirmationDialogComponent} from "./insurance-claim-confirmation-dialog.component";

const DialogContentBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.flexGap24};
    width: 100%;
`

const TopSectionBox = styled(CardBox)`
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.flexGap16};
    background-color: rgba(${props => props.theme.colors.primaryRgb}, 0.15);
    color: ${props => props.theme.colors.primaryShade};
    box-shadow: ${props => props.theme.boxShadow.shadow2};
    border-color:  rgba(${props => props.theme.colors.primaryRgb}, 0.2);;
    
`

const SectionTitleBox = styled.div`
    font-size: ${props => props.theme.fontSize.body2};
    font-weight: ${props => props.theme.fontWeight.medium};
    color: ${props => props.theme.colors.primaryShade};
`

const RefundSectionContainerBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.flexGap16};
    width: 100%;
`

const CreditRefundSectionBox = styled(RefundSectionContainerBox)`
    padding-bottom: ${props => props.theme.spacing.flexGap16};
    border-bottom: 1px solid rgba(${props => props.theme.colors.primaryRgb}, 0.2);
`

const Line1Box = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.spacing.flexGap16};
    align-items: center;
`

const Line2Box = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.spacing.flexGap16};
    align-items: center;
    font-size: ${props => props.theme.fontSize.caption};
    color: ${props => props.theme.colors.mediumTint};
`


const CreditRefundAmountBox = styled(PriceComponent)`
    font-size: ${props => props.theme.fontSize.h2};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const CreditRefundLabelBox = styled.div`
    border: 1px solid rgba(${props => props.theme.colors.primaryRgb}, 0.2);
    border-radius: ${props => props.theme.border.radiusPx(16)};
    font-size: ${props => props.theme.fontSize.smallText};
    font-weight: ${props => props.theme.fontWeight.medium};
    padding: 4px ${props => props.theme.spacing.flexGap8};
`

const TaxRefundSectionBox = styled(RefundSectionContainerBox)`
    color: ${props => props.theme.colors.medium};
`

const TaxRefundAmountBox = styled(PriceComponent)`
    font-size: ${props => props.theme.fontSize.h4};
    font-weight: ${props => props.theme.fontWeight.medium};
    color: ${props => props.theme.colors.dark};
`

const TaxRefundLabelBox = styled.div`
    border: 1px solid rgba(${props => props.theme.colors.primaryRgb}, 0.2);
    border-radius: ${props => props.theme.border.radiusPx(16)};
    font-size: ${props => props.theme.fontSize.smallText};
    font-weight: ${props => props.theme.fontWeight.medium};
    padding: 4px ${props => props.theme.spacing.flexGap8};
    background-color: ${props => props.theme.colors.lightTint};
`

const MultiUseBox = styled.div`
    display: flex;
    flex-direction: row;
    gap: 3px;
    align-items: center;
`

const ValidUntilBox = styled.div`
    display: flex;
    flex-direction: row;
    gap: 3px;
    align-items: center;
`

const IconBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.tertiary};
`

const BottomSectionBox = styled(CardBox)`
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.flexGap16};
    color: ${props => props.theme.colors.primaryShade};
    box-shadow: ${props => props.theme.boxShadow.shadow2};
`

const InfoBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
`

const InfoIconBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.primary};
    border-radius: 50%;
`
const InfoTextBox = styled.div`
    font-size: ${props => props.theme.fontSize.body2};
    color: ${props => props.theme.colors.medium};
`


const RebookingOptionsBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${props => props.theme.spacing.flexGap8};
    border-radius: ${props => props.theme.border.defaultRadius};
    background-color: ${props => props.theme.colors.notesTint};
    color: ${props => props.theme.colors.notesContrast};
    padding: ${props => props.theme.spacing.spacing16};
    font-size: ${props => props.theme.fontSize.body2};
    white-space: pre-wrap;
    width: 100%;
    
    & a {
        color: inherit;
    }
`


interface InsuranceRefundDetailsDialogProps {
    dialogHandler: IDialogConfirmationHandler;
    booking: IBookingViewModel;
    refundDetails: IInsuranceRefundDetailsResponse;
}
export const InsuranceRefundDetailsDialog: React.FC<InsuranceRefundDetailsDialogProps> = observer((props) => {
    const services = useServices();
    const purchaseAmountToRefund = props.booking.createPrice(props.refundDetails.purchaseAmountToRefund);
    const taxesAmountToRefund = props.booking.createPrice(props.refundDetails.taxesAmountToRefund);

    const onAccept = async () => {
        await props.booking.insurance.claim();
        props.dialogHandler.accept();
        services.dialog.showStandardDialog({
            render: (dialogHandler) => <InsuranceClaimConfirmationDialogComponent dialogHandler={dialogHandler} booking={props.booking} refundDetails={props.refundDetails}/>
        });
    }

    const onReject = () => {
        props.dialogHandler.reject();
    }

    const departureJourney = props.booking.departureJourney;
    if(!departureJourney) {
        return null;
    }

    const validUntilDate = services.time.formatUserFriendlyDate(services.time.addMonths(services.time.currentDate, 12));


    const rebookingOptionsText = () =>  {
        return services.language.translationFor('For rebooking options, please {aStart}Contact Aeroitalia Customer Service{aEnd}')
            .withParams({
                aStart: `<a href="${services.externalLinksConfiguration.contactUs}" target={"_blank"}>`,
                aEnd: '</a>'
            })
    };

    return (
        <StandardDialogPageComponent>
            <StandardDialogHeaderComponent dialogHandler={props.dialogHandler} title={services.language.translate('Cancel my booking')} renderTitleOnTheFirstRow={true}/>
            <StandardDialogContentComponent dialogHandler={props.dialogHandler}>
                <DialogContentBox>
                    <TopSectionBox>
                        <SectionTitleBox>
                            {services.language.translate('YOUR REFUND')}
                        </SectionTitleBox>
                        <CreditRefundSectionBox>
                            <Line1Box>
                                <CreditRefundAmountBox price={purchaseAmountToRefund}/>
                                <CreditRefundLabelBox>
                                    {services.language.translate('credit refund')}
                                </CreditRefundLabelBox>
                            </Line1Box>
                            <Line2Box>
                                <MultiUseBox>
                                    <IconBox>
                                        <IonIcon icon={repeatOutline}/>
                                    </IconBox>

                                    <span>
                                    {services.language.translate('Multi use')}
                                </span>
                                </MultiUseBox>

                                <ValidUntilBox>
                                    <IconBox>
                                        <IonIcon icon={calendarOutline}/>
                                    </IconBox>

                                    <span>{services.language.translationFor('Valid 12 months until {date}').withParams({
                                        date: validUntilDate
                                    })}</span>
                                </ValidUntilBox>


                            </Line2Box>
                        </CreditRefundSectionBox>


                        <TaxRefundSectionBox>
                            <Line1Box>
                                <TaxRefundAmountBox price={taxesAmountToRefund}/>
                                <TaxRefundLabelBox>{services.language.translate('tax refund')}</TaxRefundLabelBox>
                            </Line1Box>
                            <Line2Box>
                            <span>
                                {services.language.translate('Refunded to your original payment method.')}
                            </span>
                            </Line2Box>

                        </TaxRefundSectionBox>

                    </TopSectionBox>
                    <BottomSectionBox>


                        <InfoBox>
                            <InfoIconBox>
                                <IonIcon icon={informationCircleOutline}/>
                            </InfoIconBox>
                            <InfoTextBox>{services.language.translate('All flights for all segments and all passengers in this booking will be cancelled.')}</InfoTextBox>
                        </InfoBox>

                        <RebookingOptionsBox >
                            <div dangerouslySetInnerHTML={{__html: rebookingOptionsText()}}/>
                            <IonIcon icon={arrowForwardOutline}/>
                        </RebookingOptionsBox>

                    </BottomSectionBox>
                </DialogContentBox>

            </StandardDialogContentComponent>

            <StandardDialogFooterComponent dialogHandler={props.dialogHandler}>
                <NeutralButton onClick={onReject}>
                    {services.language.translate('Back to my booking details')}
                </NeutralButton>

                <PrimaryButton onClick={onAccept}>
                    {services.language.translate('Yes, cancel my booking')}
                </PrimaryButton>
            </StandardDialogFooterComponent>
        </StandardDialogPageComponent>
    );
});